import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of, tap, throwError } from 'rxjs';
import { ApiResponse } from '../../../../../shared/dtos/api-response';
import { ApiError } from '../../../../../shared/dtos/api-error';
import {
  EmployeeMapper,
  PermissionGroupMapper,
  type Employee,
  type PermissionGroup,
} from '../../models/employee';
import {
  EmployeesStore,
  GrantablePermissionsStore,
  PermissionGroupsStore,
} from '../../stores/employees.store';
import {
  INTERNAL_EMPLOYEE_GROUP_ASSIGN_ENDPOINT,
  INTERNAL_EMPLOYEE_GROUPS_ENDPOINT,
  INTERNAL_EMPLOYEE_PERMISSIONS_ENDPOINT,
  INTERNAL_EMPLOYEE_PROFILE_UPDATE_ENDPOINT,
  INTERNAL_EMPLOYEES_ENDPOINT,
} from '../../constants/endpoints';
import type {
  EmployeeGroupAssignmentRequest,
  EmployeeInfo,
  EmployeeProfileUpdateRequest,
  PermissionGroupInfo,
} from '../../dtos/employee';
import { startAuthenticatedPolling } from '../../../../../shared/utils/authenticated-polling';

@Injectable({ providedIn: 'root' })
export class EmployeeService {
  private readonly http = inject(HttpClient);
  private readonly employeesStore = inject(EmployeesStore);
  private readonly groupsStore = inject(PermissionGroupsStore);
  private readonly permissionsStore = inject(GrantablePermissionsStore);

  constructor() {
    startAuthenticatedPolling(() => this.getEmployees());
  }

  getEmployees(): Observable<Employee[]> {
    this.employeesStore.setLoading();
    return this.http.get<ApiResponse<EmployeeInfo[]>>(INTERNAL_EMPLOYEES_ENDPOINT).pipe(
      map((resp) => EmployeeMapper.fromDtoList(resp.data)),
      tap((records) => this.employeesStore.setRecords(records)),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 404) {
          this.employeesStore.setRecords([]);
          return of([]);
        }
        const body = err.error as ApiError | undefined;
        this.employeesStore.setError(body?.message ?? 'Failed to load employees.');
        return throwError(() => body ?? { message: 'Failed to load employees.' });
      }),
    );
  }

  getPermissionGroups(): Observable<PermissionGroup[]> {
    this.groupsStore.setLoading();
    return this.http.get<ApiResponse<PermissionGroupInfo[]>>(INTERNAL_EMPLOYEE_GROUPS_ENDPOINT).pipe(
      map((resp) => PermissionGroupMapper.fromDtoList(resp.data)),
      tap((records) => this.groupsStore.setRecords(records)),
      catchError((err: HttpErrorResponse) => {
        const body = err.error as ApiError | undefined;
        this.groupsStore.setError(body?.message ?? 'Failed to load permission groups.');
        return throwError(() => body ?? { message: 'Failed to load permission groups.' });
      }),
    );
  }

  getGrantablePermissions(): Observable<string[]> {
    this.permissionsStore.setLoading();
    return this.http.get<ApiResponse<string[]>>(INTERNAL_EMPLOYEE_PERMISSIONS_ENDPOINT).pipe(
      map((resp) => resp.data ?? []),
      tap((permissions) => this.permissionsStore.setData(permissions)),
      catchError((err: HttpErrorResponse) => {
        const body = err.error as ApiError | undefined;
        this.permissionsStore.setError(body?.message ?? 'Failed to load permissions.');
        return throwError(() => body ?? { message: 'Failed to load permissions.' });
      }),
    );
  }

  assignGroup(userToUpdateId: number, body: EmployeeGroupAssignmentRequest): Observable<Employee> {
    return this.http
      .put<ApiResponse<EmployeeInfo>>(INTERNAL_EMPLOYEE_GROUP_ASSIGN_ENDPOINT(userToUpdateId), body)
      .pipe(
        map((resp) => EmployeeMapper.fromDto(resp.data)),
        tap((record) => this.employeesStore.updateRecord(record)),
        catchError((err: HttpErrorResponse) => {
          const body = err.error as ApiError | undefined;
          return throwError(() => body ?? { message: 'Failed to assign group.' });
        }),
      );
  }

  updateProfile(userToUpdateId: number, body: EmployeeProfileUpdateRequest): Observable<Employee> {
    return this.http
      .put<ApiResponse<EmployeeInfo>>(INTERNAL_EMPLOYEE_PROFILE_UPDATE_ENDPOINT(userToUpdateId), body)
      .pipe(
        map((resp) => EmployeeMapper.fromDto(resp.data)),
        tap((record) => this.employeesStore.updateRecord(record)),
        catchError((err: HttpErrorResponse) => {
          const body = err.error as ApiError | undefined;
          return throwError(() => body ?? { message: 'Failed to update profile.' });
        }),
      );
  }
}
